import { useTranslation } from 'react-i18next';
import { Bar, BarChart, CartesianGrid, Cell, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { IUtilizationDay } from '@apis/equipment';
import { TrendingIcon } from '@utils/icons';
import { InfoTip } from '@components/ui/InfoTip/InfoTip';
import { MarkerPopup, TooltipContent } from '@components/ui/Tooltip/Tooltip';
import { toRows, type IUtilizationRow } from './utilizationRows';
import styles from './WeeklyUtilization.module.css';
import detailStyles from '../AssetDetail.module.css';

interface IWeeklyUtilizationProps {
  isLoading: boolean;
  isError: boolean;
  utilization: IUtilizationDay[];
  /** The site's today, when its bar is in the week — that bar is still filling. */
  todayDate?: string;
}

const CHART_HEIGHT = 120;
const BAR_RADIUS: [number, number, number, number] = [4, 4, 0, 0];
const AXIS_TICK = { fontSize: 10, fill: 'var(--g500)' } as const;
const GRID_STROKE = 'var(--separator)';
const PCT_TICKS = [0, 50, 100];

interface IUtilizationTooltipProps {
  active?: boolean;
  payload?: { payload?: IUtilizationRow }[];
}

/** The hovered day: its date, then what the bar stands for in hours and share. */
export function UtilizationTooltip({ active, payload }: IUtilizationTooltipProps) {
  const row = payload?.[0]?.payload;
  if (!active || !row) return null;
  return (
    <MarkerPopup>
      <TooltipContent title={row.tipTitle} lines={row.tipLines} />
    </MarkerPopup>
  );
}

// Hoisted: Recharts clones the element with the hover props, so one instance
// avoids a fresh element per render (same as the other charts).
const TOOLTIP = <UtilizationTooltip />;

export function WeeklyUtilization({ isLoading, isError, utilization, todayDate }: IWeeklyUtilizationProps) {
  const { t, i18n } = useTranslation();
  const anyData = utilization.some((d) => d.hasData);
  // A day still on its way gets a placeholder bar, so the chart is up before it lands.
  const drawable = anyData || utilization.some((d) => d.pending);
  // With no bar to draw, a day that failed makes the week unavailable, not empty.
  const unavailable = isError || (!drawable && utilization.some((d) => d.unavailable));
  const measured = utilization.filter((d) => d.hasData);
  const avgPct = measured.length
    ? Math.round(measured.reduce((sum, d) => sum + d.utilizationPct, 0) / measured.length)
    : null;
  const showChart = !isLoading && !isError && drawable;

  return (
    <section className={detailStyles.section}>
      <div className={detailStyles.sectionTitleRow}>
        <span className={detailStyles.trendingIcon}><TrendingIcon size={16} /></span>
        <h4 className={detailStyles.sectionTitle}>{t('equipment.detail.weeklyUtilization')}</h4>
        <InfoTip text={t('equipment.detail.utilizationInfo')} />
        {showChart && avgPct !== null && (
          <span className={styles.average}>{t('equipment.detail.utilizationAvg', { pct: avgPct })}</span>
        )}
      </div>
      {isLoading && (
        <p className={detailStyles.pendingNote}>{t('equipment.detail.utilizationLoading')}</p>
      )}
      {!isLoading && unavailable && (
        <p className={detailStyles.pendingNote}>{t('equipment.detail.utilizationUnavailable')}</p>
      )}
      {!isLoading && !unavailable && !drawable && (
        <p className={detailStyles.pendingNote}>{t('equipment.detail.utilizationEmpty')}</p>
      )}
      {showChart && (
        <div className={styles.chart}>
          <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
            <BarChart
              data={toRows(utilization, todayDate, t, i18n.language)}
              margin={{ top: 16, right: 4, bottom: 0, left: 0 }}
            >
              <CartesianGrid vertical={false} stroke={GRID_STROKE} />
              <XAxis dataKey="axisLabel" tick={AXIS_TICK} tickLine={false} axisLine={{ stroke: GRID_STROKE }} interval={0} />
              <YAxis
                domain={[0, 100]}
                ticks={PCT_TICKS}
                tickFormatter={(v: number) => `${v}%`}
                tick={AXIS_TICK}
                tickLine={false}
                axisLine={false}
                width={34}
              />
              <Tooltip content={TOOLTIP} cursor={{ fill: 'var(--info-tint-soft)' }} />
              {/* One stack, so a failed or loading day's mark shares its slot
                  instead of sitting beside an empty twin (the waste chart's trick). */}
              <Bar dataKey="pct" stackId="day" fill="var(--info)" radius={BAR_RADIUS} isAnimationActive={false} maxBarSize={22}>
                {utilization.map((d) => (
                  // Today is still filling — lighter, so it doesn't read as a finished low day.
                  <Cell key={d.date} fillOpacity={d.date === todayDate ? 0.45 : 1} />
                ))}
                <LabelList dataKey="valueLabel" position="top" className={styles.valueLabel} />
              </Bar>
              <Bar
                dataKey="missing"
                stackId="day"
                fill="transparent"
                stroke="var(--g300)"
                strokeDasharray="3 3"
                radius={BAR_RADIUS}
                isAnimationActive={false}
                maxBarSize={22}
              />
              <Bar
                dataKey="loading"
                stackId="day"
                fill="var(--g100)"
                className={styles.loadingBar}
                radius={BAR_RADIUS}
                isAnimationActive={false}
                maxBarSize={22}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </section>
  );
}
